/**
 * MeasurementEntryForm Component
 *
 * Form for entering body measurements with validation and API integration.
 * Handles required fields (weight, height, chest, waist, hip) with unit selection.
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import type { MeasurementInput, WeightUnit, LengthUnit, ListMeasurementsResponse } from '../../types/measurements';
import { VALIDATION_RANGES } from '../../types/measurements';
import { useToast } from '../Toast';

interface FormErrors {
  [key: string]: string;
}

const MeasurementEntryForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [formData, setFormData] = useState({
    weight_value: '',
    weight_unit: 'kg' as WeightUnit,
    height_value: '',
    height_unit: 'cm' as LengthUnit,
    chest_value: '',
    chest_unit: 'cm' as LengthUnit,
    waist_value: '',
    waist_unit: 'cm' as LengthUnit,
    hip_value: '',
    hip_unit: 'cm' as LengthUnit,
    // Optional measurements
    left_arm_value: '',
    left_arm_unit: 'cm' as LengthUnit,
    right_arm_value: '',
    right_arm_unit: 'cm' as LengthUnit,
    left_thigh_value: '',
    left_thigh_unit: 'cm' as LengthUnit,
    right_thigh_value: '',
    right_thigh_unit: 'cm' as LengthUnit,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showOptional, setShowOptional] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [currentMeasurementId, setCurrentMeasurementId] = useState<string | null>(null);
  const [waistHipRatio, setWaistHipRatio] = useState<number | null>(null);

  // Fetch today's measurement
  const { data: todayData } = useQuery<ListMeasurementsResponse>({
    queryKey: ['measurements', 'today'],
    queryFn: async () => {
      return apiService.get('/measurements?days=1&limit=1');
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Auto-load today's measurement when data arrives
  useEffect(() => {
    if (todayData && todayData.measurements.length > 0) {
      const todayMeasurement = todayData.measurements[0];
      // Check if it's actually today's measurement
      const measurementDate = new Date(todayMeasurement.date).toDateString();
      const today = new Date().toDateString();

      if (measurementDate === today) {
        const m = todayMeasurement.measurements;
        setFormData({
          weight_value: m.weight.value.toString(),
          weight_unit: m.weight.unit as WeightUnit,
          height_value: m.height.value.toString(),
          height_unit: m.height.unit as LengthUnit,
          chest_value: m.chest.value.toString(),
          chest_unit: m.chest.unit as LengthUnit,
          waist_value: m.waist.value.toString(),
          waist_unit: m.waist.unit as LengthUnit,
          hip_value: m.hip.value.toString(),
          hip_unit: m.hip.unit as LengthUnit,
          left_arm_value: m.left_arm?.value.toString() || '',
          left_arm_unit: (m.left_arm?.unit as LengthUnit) || 'cm',
          right_arm_value: m.right_arm?.value.toString() || '',
          right_arm_unit: (m.right_arm?.unit as LengthUnit) || 'cm',
          left_thigh_value: m.left_thigh?.value.toString() || '',
          left_thigh_unit: (m.left_thigh?.unit as LengthUnit) || 'cm',
          right_thigh_value: m.right_thigh?.value.toString() || '',
          right_thigh_unit: (m.right_thigh?.unit as LengthUnit) || 'cm',
        });
        if (m.left_arm || m.right_arm || m.left_thigh || m.right_thigh) {
          setShowOptional(true);
        }
        setCurrentMeasurementId(todayMeasurement.id || todayMeasurement._id || null);
        setWaistHipRatio(todayMeasurement.waist_hip_ratio);
        setIsSaved(true);
      }
    }
  }, [todayData]);

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Weight validation
    const weight = parseFloat(formData.weight_value);
    if (isNaN(weight)) {
      newErrors.weight = 'Weight is required';
    } else {
      const range = formData.weight_unit === 'kg'
        ? VALIDATION_RANGES.weight.kg
        : VALIDATION_RANGES.weight.lbs;
      if (weight < range.min || weight > range.max) {
        newErrors.weight = `Weight must be between ${range.min}-${range.max} ${formData.weight_unit}`;
      }
    }

    // Height validation
    const height = parseFloat(formData.height_value);
    if (isNaN(height)) {
      newErrors.height = 'Height is required';
    } else {
      const range = formData.height_unit === 'cm'
        ? VALIDATION_RANGES.height.cm
        : VALIDATION_RANGES.height.inches;
      if (height < range.min || height > range.max) {
        newErrors.height = `Height must be between ${range.min}-${range.max} ${formData.height_unit}`;
      }
    }

    // Circumference validation (chest, waist, hip) - required
    const circumferences = ['chest', 'waist', 'hip'] as const;
    circumferences.forEach((field) => {
      const value = parseFloat(formData[`${field}_value`]);
      const unit = formData[`${field}_unit`];
      if (isNaN(value)) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      } else {
        const range = unit === 'cm'
          ? VALIDATION_RANGES.circumference.cm
          : VALIDATION_RANGES.circumference.inches;
        if (value < range.min || value > range.max) {
          newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} must be between ${range.min}-${range.max} ${unit}`;
        }
      }
    });

    // Optional circumference validation (arms, thighs) - only validate if provided
    const optionalCircumferences = ['left_arm', 'right_arm', 'left_thigh', 'right_thigh'] as const;
    optionalCircumferences.forEach((field) => {
      const value = parseFloat(formData[`${field}_value`]);
      const unit = formData[`${field}_unit`];
      // Only validate if user provided a value
      if (!isNaN(value) && formData[`${field}_value`] !== '') {
        const range = unit === 'cm'
          ? VALIDATION_RANGES.circumference.cm
          : VALIDATION_RANGES.circumference.inches;
        if (value < range.min || value > range.max) {
          const fieldName = field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          newErrors[field] = `${fieldName} must be between ${range.min}-${range.max} ${unit}`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Mutation for creating or updating measurement
  const saveMutation = useMutation({
    mutationFn: async (data: MeasurementInput) => {
      if (currentMeasurementId) {
        return apiService.patch(`/measurements/${currentMeasurementId}`, data);
      }
      return apiService.post('/measurements', data);
    },
    onSuccess: (data) => {
      success('Body measurements successfully saved');

      // Update measurement ID, WHR, and mark as saved
      setCurrentMeasurementId(data.id || data._id);
      setWaistHipRatio(data.waist_hip_ratio);
      setIsSaved(true);

      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
    onError: (err: any) => {
      if (err.status === 409) {
        showError('A measurement already exists for today. Please edit your existing measurement instead.');
      } else if (err.status === 422) {
        const detail = err.data?.detail || 'Validation failed';
        if (detail.includes('changed by')) {
          showError(`${detail} Click save again to confirm.`);
        } else {
          showError(detail);
        }
      } else {
        showError('Failed to save measurement. Please try again.');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const measurementData: MeasurementInput = {
      measurements: {
        weight: {
          value: parseFloat(formData.weight_value),
          unit: formData.weight_unit,
        },
        height: {
          value: parseFloat(formData.height_value),
          unit: formData.height_unit,
        },
        chest: {
          value: parseFloat(formData.chest_value),
          unit: formData.chest_unit,
        },
        waist: {
          value: parseFloat(formData.waist_value),
          unit: formData.waist_unit,
        },
        hip: {
          value: parseFloat(formData.hip_value),
          unit: formData.hip_unit,
        },
        // Include optional measurements only if provided
        ...(formData.left_arm_value && {
          left_arm: {
            value: parseFloat(formData.left_arm_value),
            unit: formData.left_arm_unit,
          },
        }),
        ...(formData.right_arm_value && {
          right_arm: {
            value: parseFloat(formData.right_arm_value),
            unit: formData.right_arm_unit,
          },
        }),
        ...(formData.left_thigh_value && {
          left_thigh: {
            value: parseFloat(formData.left_thigh_value),
            unit: formData.left_thigh_unit,
          },
        }),
        ...(formData.right_thigh_value && {
          right_thigh: {
            value: parseFloat(formData.right_thigh_value),
            unit: formData.right_thigh_unit,
          },
        }),
      },
    };

    saveMutation.mutate(measurementData);
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsSaved(false); // Mark as unsaved when user makes changes
    // Clear error for this field when user starts typing
    if (errors[field.replace('_value', '').replace('_unit', '')]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field.replace('_value', '').replace('_unit', '')];
        return newErrors;
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-soft p-6">
      <h2 className="text-2xl font-serif font-bold text-primary-700 mb-6">
        Record Body Measurements
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Weight */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Weight *
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.1"
              value={formData.weight_value}
              onChange={(e) => handleChange('weight_value', e.target.value)}
              className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.weight ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="65.5"
            />
            <select
              value={formData.weight_unit}
              onChange={(e) => handleChange('weight_unit', e.target.value)}
              className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
            >
              <option value="kg">kg</option>
              <option value="lbs">lbs</option>
            </select>
          </div>
          {errors.weight && (
            <p className="mt-1 text-sm text-red-600">{errors.weight}</p>
          )}
        </div>

        {/* Height */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Height *
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.1"
              value={formData.height_value}
              onChange={(e) => handleChange('height_value', e.target.value)}
              className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.height ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="165.0"
            />
            <select
              value={formData.height_unit}
              onChange={(e) => handleChange('height_unit', e.target.value)}
              className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
            >
              <option value="cm">cm</option>
              <option value="inches">inches</option>
            </select>
          </div>
          {errors.height && (
            <p className="mt-1 text-sm text-red-600">{errors.height}</p>
          )}
        </div>

        {/* Chest */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Chest *
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.1"
              value={formData.chest_value}
              onChange={(e) => handleChange('chest_value', e.target.value)}
              className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.chest ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="95.0"
            />
            <select
              value={formData.chest_unit}
              onChange={(e) => handleChange('chest_unit', e.target.value)}
              className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
            >
              <option value="cm">cm</option>
              <option value="inches">inches</option>
            </select>
          </div>
          {errors.chest && (
            <p className="mt-1 text-sm text-red-600">{errors.chest}</p>
          )}
        </div>

        {/* Waist */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Waist *
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.1"
              value={formData.waist_value}
              onChange={(e) => handleChange('waist_value', e.target.value)}
              className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.waist ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="75.0"
            />
            <select
              value={formData.waist_unit}
              onChange={(e) => handleChange('waist_unit', e.target.value)}
              className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
            >
              <option value="cm">cm</option>
              <option value="inches">inches</option>
            </select>
          </div>
          {errors.waist && (
            <p className="mt-1 text-sm text-red-600">{errors.waist}</p>
          )}
        </div>

        {/* Hip */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Hip *
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.1"
              value={formData.hip_value}
              onChange={(e) => handleChange('hip_value', e.target.value)}
              className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.hip ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="100.0"
            />
            <select
              value={formData.hip_unit}
              onChange={(e) => handleChange('hip_unit', e.target.value)}
              className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
            >
              <option value="cm">cm</option>
              <option value="inches">inches</option>
            </select>
          </div>
          {errors.hip && (
            <p className="mt-1 text-sm text-red-600">{errors.hip}</p>
          )}
        </div>

        {/* Optional Measurements Section */}
        <div className="border-t border-neutral-200 pt-6">
          <button
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            className="flex items-center justify-between w-full text-left text-sm font-medium text-neutral-700 hover:text-primary-700 transition"
          >
            <span className="flex items-center gap-2">
              <span className="text-lg">{showOptional ? '▼' : '▶'}</span>
              Additional Measurements (Optional)
            </span>
            <span className="text-xs text-neutral-500">
              Arms & Thighs
            </span>
          </button>

          {showOptional && (
            <div className="mt-6 space-y-6 pl-6 border-l-2 border-primary-200">
              {/* Left Arm */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Left Arm
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={formData.left_arm_value}
                    onChange={(e) => handleChange('left_arm_value', e.target.value)}
                    className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.left_arm ? 'border-red-500' : 'border-neutral-300'
                    }`}
                    placeholder="28.0"
                  />
                  <select
                    value={formData.left_arm_unit}
                    onChange={(e) => handleChange('left_arm_unit', e.target.value)}
                    className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="cm">cm</option>
                    <option value="inches">inches</option>
                  </select>
                </div>
                {errors.left_arm && (
                  <p className="mt-1 text-sm text-red-600">{errors.left_arm}</p>
                )}
              </div>

              {/* Right Arm */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Right Arm
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={formData.right_arm_value}
                    onChange={(e) => handleChange('right_arm_value', e.target.value)}
                    className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.right_arm ? 'border-red-500' : 'border-neutral-300'
                    }`}
                    placeholder="28.5"
                  />
                  <select
                    value={formData.right_arm_unit}
                    onChange={(e) => handleChange('right_arm_unit', e.target.value)}
                    className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="cm">cm</option>
                    <option value="inches">inches</option>
                  </select>
                </div>
                {errors.right_arm && (
                  <p className="mt-1 text-sm text-red-600">{errors.right_arm}</p>
                )}
              </div>

              {/* Left Thigh */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Left Thigh
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={formData.left_thigh_value}
                    onChange={(e) => handleChange('left_thigh_value', e.target.value)}
                    className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.left_thigh ? 'border-red-500' : 'border-neutral-300'
                    }`}
                    placeholder="55.0"
                  />
                  <select
                    value={formData.left_thigh_unit}
                    onChange={(e) => handleChange('left_thigh_unit', e.target.value)}
                    className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="cm">cm</option>
                    <option value="inches">inches</option>
                  </select>
                </div>
                {errors.left_thigh && (
                  <p className="mt-1 text-sm text-red-600">{errors.left_thigh}</p>
                )}
              </div>

              {/* Right Thigh */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Right Thigh
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={formData.right_thigh_value}
                    onChange={(e) => handleChange('right_thigh_value', e.target.value)}
                    className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.right_thigh ? 'border-red-500' : 'border-neutral-300'
                    }`}
                    placeholder="54.0"
                  />
                  <select
                    value={formData.right_thigh_unit}
                    onChange={(e) => handleChange('right_thigh_unit', e.target.value)}
                    className="px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="cm">cm</option>
                    <option value="inches">inches</option>
                  </select>
                </div>
                {errors.right_thigh && (
                  <p className="mt-1 text-sm text-red-600">{errors.right_thigh}</p>
                )}
              </div>

              <p className="text-xs text-neutral-500 italic">
                Optional measurements help track detailed changes in specific areas, particularly useful for strength training or targeted fitness goals.
              </p>
            </div>
          )}
        </div>

        {/* Success Message */}
        {isSaved && waistHipRatio && (
          <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded-r-lg">
            <p className="text-sm text-green-800 font-medium">✓ Body measurements successfully saved</p>
            <p className="text-sm text-green-700 mt-1">
              Waist-Hip Ratio (WHR): <span className="font-semibold">{waistHipRatio.toFixed(2)}</span>
            </p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="w-full btn btn-primary py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saveMutation.isPending
            ? 'Saving...'
            : isSaved
            ? 'Edit Measurement'
            : 'Save Measurement'
          }
        </button>
      </form>
    </div>
  );
};

export default MeasurementEntryForm;
